// The coach's device diagnostics on the phone (engine/src/coach/diagnostics.ts): the mic session
// (live/session.ts), the model's self-test (model/client.ts), the lips camera (lips/) and the playback A/B
// answer (the diagnostics page) each record what they measured; it waits in localStorage and goes with the
// next health report (health/report.ts takeCoachDiagnostics), then leaves the device. A report holds only what
// was measured since the last one: the Worker merges it into the device's kept diagnostics (engine
// mergeCoachDiagnostics), so a report with only the self-test does not wipe the mic's numbers. Numbers and
// fixed words only: the input's name is kept as its kind (micKind). Light: imported by the health report.
import { mergeCoachDiagnostics, micKind, type CoachDiagnostics } from '@valyrio/engine/coach/diagnostics';
import type { MicReport } from '@valyrio/engine/coach/contract';

const KEY = 'valyrio:coach-diagnostics';

/** What waits to be sent (null: nothing new since the last report). */
export function pendingCoachDiagnostics(): Partial<CoachDiagnostics> | null {
  try {
    const j = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<CoachDiagnostics> | null;
    return j && typeof j === 'object' && !Array.isArray(j) ? j : null;
  } catch {
    return null;
  }
}

/** The mic's report as the diagnostics keep it: the label as its kind. */
export function micForDiagnostics(r: MicReport): NonNullable<CoachDiagnostics['mic']> {
  return { ...r, deviceLabel: micKind(r.deviceLabel) };
}

type Patch = Partial<Omit<CoachDiagnostics, 'mic' | 'device' | 'at'>> & { mic?: MicReport };

function write(d: Partial<CoachDiagnostics>): void {
  localStorage.setItem(KEY, JSON.stringify(d));
}

/** Merge new measurements into what waits (engine mergeCoachDiagnostics); counters add up. Never throws. */
export function recordCoachDiagnostics(patch: Patch, now: number = Date.now()): void {
  try {
    const { mic, ...rest } = patch;
    const next: Partial<CoachDiagnostics> = { ...rest, ...(mic ? { mic: micForDiagnostics(mic) } : {}), at: new Date(now).toISOString() };
    write(mergeCoachDiagnostics(pendingCoachDiagnostics(), next));
  } catch {
    /* no storage: diagnostics are best effort */
  }
}

/** For the health report: what waits, with this device's id; it leaves the device once queued (clearCoachDiagnostics). */
export function takeCoachDiagnostics(device: string): Partial<CoachDiagnostics> | null {
  const p = pendingCoachDiagnostics();
  return p ? { ...p, device } : null;
}

// ---------- the playback A/B answer, kept on this device ----------
// (the pending diagnostics leave the device with the health report; the live screen needs the answer later)

export type PlaybackAB = 'same' | 'worse-with-mic' | 'unsure';
const AB_KEY = 'valyrio:coach-playback-ab';

/** The diagnostics page's answer: does Peterson's clip sound worse with the mic open on this device? null: not asked. */
export function playbackAB(): PlaybackAB | null {
  try {
    const v = localStorage.getItem(AB_KEY);
    return v === 'same' || v === 'worse-with-mic' || v === 'unsure' ? v : null;
  } catch {
    return null;
  }
}

/** Keep the answer on the device and send it with the next health report. */
export function keepPlaybackAB(v: PlaybackAB): void {
  try {
    localStorage.setItem(AB_KEY, v);
  } catch {
    /* the live screen then decides by the browser */
  }
  recordCoachDiagnostics({ playbackAB: v });
}

/**
 * Whether Peterson's clip closes the mic on this device (WebKit 311451: playback degrades while the mic is open).
 * On WebKit (iPhone, iPad, Mac Safari) it closes unless the person heard no difference; elsewhere only when they
 * heard it get worse. Otherwise the takes only pause while he speaks.
 */
export function closesMicForPlayback(ab: PlaybackAB | null, webkit: boolean): boolean {
  if (ab === 'same') return false;
  if (ab === 'worse-with-mic') return true;
  return webkit;
}

/** WebKit's audio stack: every iOS browser (an iPad says MacIntel with touch), and Safari on a Mac. */
export function isWebKit(ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent, touchMac?: boolean): boolean {
  const ipad = touchMac ?? (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (/iPad|iPhone|iPod/.test(ua) || ipad) return true;
  return /AppleWebKit/.test(ua) && /Safari\//.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR|Firefox|Android/.test(ua);
}

/**
 * After the report was queued: what was sent leaves the device. A measurement recorded meanwhile (a newer `at`)
 * stays, with the counters that were sent taken off (the Worker adds them up: never twice); the rest of it is
 * sent again, which the Worker's merge makes harmless.
 */
export function clearCoachDiagnostics(sent: Partial<CoachDiagnostics>): void {
  try {
    const p = pendingCoachDiagnostics();
    if (!p) return;
    if (p.at === sent.at) {
      localStorage.removeItem(KEY);
      return;
    }
    write({
      ...p,
      interruptions: Math.max(0, (p.interruptions ?? 0) - (sent.interruptions ?? 0)),
      routeChanges: Math.max(0, (p.routeChanges ?? 0) - (sent.routeChanges ?? 0)),
    });
  } catch {
    /* none */
  }
}
